<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-2">
      <v-icon class="mr-3" color="primary">mdi-eye</v-icon>
      <span class="text-h5 font-weight-bold">Visibility Analysis</span>
    </div>
  </div>
  <div class="pa-6">
      <!-- Brand match strings -->
      <v-card v-if="primaryBrandName" variant="outlined" class="brand-match-card mb-4">
        <v-card-text class="pa-4">
          <div class="brand-match-header d-flex align-start justify-space-between flex-wrap ga-3 mb-2">
            <div class="brand-match-header__copy min-width-0">
              <div class="text-subtitle-2 font-weight-bold">Brand match strings</div>
              <div class="text-caption text-medium-emphasis mt-1">
                Alternate names counted as your brand in visibility scoring.
                Recalculate applies them across all LLMs.
              </div>
            </div>
            <v-btn
              variant="tonal"
              color="primary"
              size="small"
              class="text-none flex-shrink-0"
              prepend-icon="mdi-refresh"
              :loading="rescoringVisibility"
              :disabled="rescoringVisibility || savingAliases || !reviewEntityId"
              @click="rescoreVisibility"
            >
              Recalculate
            </v-btn>
          </div>

          <div
            class="brand-match-editor"
            :class="{ 'brand-match-editor--disabled': savingAliases || !reviewEntityId }"
            @click="focusAliasInput"
          >
            <div class="brand-match-editor__inner">
              <v-chip
                color="primary"
                variant="flat"
                size="small"
                class="brand-match-chip brand-match-chip--primary flex-shrink-0"
              >
                {{ primaryBrandName }}
              </v-chip>
              <v-chip
                v-for="alias in localAliases"
                :key="alias"
                closable
                size="small"
                variant="outlined"
                class="brand-match-chip flex-shrink-0"
                :disabled="savingAliases"
                @click:close.stop="removeAlias(alias)"
              >
                {{ alias }}
              </v-chip>
              <input
                ref="aliasInputRef"
                v-model="newAliasInput"
                type="text"
                class="brand-match-editor__input"
                placeholder="Add alternate name…"
                :disabled="savingAliases || !reviewEntityId"
                @keydown.enter.prevent="addAlias"
              />
              <button
                type="button"
                class="brand-match-editor__add"
                :disabled="!canAddAlias"
                aria-label="Add alternate name"
                @click.stop="addAlias"
              >
                <v-icon size="18">mdi-plus</v-icon>
              </button>
            </div>
          </div>

          <div v-if="aliasError" class="text-caption text-error mt-2">{{ aliasError }}</div>
          <div
            v-else-if="rescoreMessage"
            class="text-caption mt-2"
            :class="rescoreError ? 'text-error' : 'text-medium-emphasis'"
          >
            {{ rescoreMessage }}
          </div>
        </v-card-text>
      </v-card>

      <!-- Persona Filter Dropdown -->
      <v-row v-if="hasData && personaOptions.length > 0" class="mb-4">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedPersona"
            :items="personaOptions"
            label="Filter by Persona"
            variant="outlined"
            density="comfortable"
            hide-details
          ></v-select>
        </v-col>
      </v-row>

      <HistoricalDateBanner
        :visible="showSnapshotBanner"
        :loading="loadingSnapshot"
        :load-target="snapshotLoadTarget"
        :selected-date="selectedHistoricalDate"
        :error="snapshotError"
        @clear="$emit('historical-date-cleared')"
      />

      <v-row v-if="!hasData" class="text-center py-8">
        <v-col cols="12">
          <v-alert type="info" variant="tonal">
            No query data available for visibility analysis.
          </v-alert>
        </v-col>
      </v-row>

      <template v-else>
        <!-- Metrics Overview -->
        <v-row class="mb-6">
          <v-col cols="12" md="4">
            <v-card variant="outlined" class="metric-card">
              <v-card-text class="text-center pa-6">
                <div class="text-h3 font-weight-bold mb-2" :class="getCategoryOwnershipColorClass(categoryOwnershipScore)">
                  {{ categoryOwnershipScore.toFixed(1) }}%
                </div>
                <div class="text-subtitle-1 font-weight-bold d-flex align-center justify-center">
                  Category Ownership
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey" class="ml-1">mdi-information-outline</v-icon>
                    </template>
                    <span>Combined score: AI ranking (60%) + Share of voice (40%)</span>
                  </v-tooltip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card variant="outlined" class="metric-card">
              <v-card-text class="text-center pa-6">
                <div class="text-h3 font-weight-bold mb-2" :class="getCoverageColorClass(assessmentPromptCoverageRate)">
                  {{ assessmentPromptCoverageRate }}%
                </div>
                <div class="text-subtitle-1 font-weight-bold d-flex align-center justify-center">
                  Share of Voice
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey" class="ml-1">mdi-information-outline</v-icon>
                    </template>
                    <span>Percentage of queries where your brand is mentioned</span>
                  </v-tooltip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card variant="outlined" class="metric-card">
              <v-card-text class="text-center pa-6">
                <div class="text-h3 font-weight-bold mb-2" :class="getRankingPositionColorClass(averageRankingPosition)">
                  {{ averageRankingPosition > 0 ? `#${averageRankingPosition.toFixed(1)}` : 'Not Ranked' }}
                </div>
                <div class="text-subtitle-1 font-weight-bold mb-2 d-flex align-center justify-center">
                  AI Ranking Position
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="grey" class="ml-1">mdi-information-outline</v-icon>
                    </template>
                    <span>Average ranking position when mentioned</span>
                  </v-tooltip>
                </div>
                <div v-if="unrankedQueriesCount > 0" class="text-body-2 text-error font-weight-bold">
                  Not mentioned in {{ unrankedQueriesCount }} {{ unrankedQueriesCount === 1 ? 'response' : 'responses' }}!
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Historical Category Ownership Chart -->
        <v-row class="mb-6">
          <v-col cols="12">
            <div v-if="lacksHistoricalTrendAccess" class="upgrade-gate-banner brand-gate-card mb-4">
              <div class="d-flex align-center justify-space-between flex-wrap ga-3">
                <div>
                  <div class="font-weight-bold mb-1 brand-gate-card__title">To see historical data over time</div>
                  <div class="text-body-2 brand-gate-card__body">
                    Upgrade to unlock trend charts and ongoing tracking.
                  </div>
                </div>
                <v-btn
                  variant="flat"
                  class="brand-upgrade-cta-btn text-none font-weight-bold"
                  @click="$router.push('/profile?tab=billing')"
                >
                  Upgrade Your Plan
                </v-btn>
              </div>
            </div>
            <v-card variant="outlined" class="historical-chart-card">
              <v-card-title class="d-flex flex-column align-center justify-center">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
                  Category Ownership Over Time
                </div>
                <div v-if="currentHistoricalData.length > 1" class="text-caption text-medium-emphasis font-weight-regular mt-1">
                  Click a data point to view scores for that date
                </div>
              </v-card-title>
              <v-card-text>
                <div v-if="currentHistoricalData.length <= 1" class="text-center py-12">
                  <v-icon color="grey" size="64" class="mb-4">mdi-chart-line-variant</v-icon>
                  <div class="text-h6 mb-2 text-medium-emphasis">Data not available</div>
                  <div class="d-flex align-center justify-center">
                    <v-icon color="info" size="20" class="mr-2">mdi-information</v-icon>
                    <span class="text-body-2 text-medium-emphasis">
                      Historical data is being collected, this takes ~2 days to start tracking historically.
                    </span>
                  </div>
                </div>
                <div v-else id="categoryOwnershipChart" style="height: 300px;"></div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-expansion-panels v-model="expansionPanel" class="mt-4" variant="accordion">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon class="mr-2" color="primary">mdi-chart-bar</v-icon>
              Brand Rankings Heatmap
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div v-if="!primaryBrandName" class="mb-4">
                <v-alert type="info" variant="tonal">
                  Brand name not available for heatmap display.
                </v-alert>
              </div>
              <template v-else>
                <div 
                  v-for="(personaGroup, personaIndex) in personaHeatmapGroups" 
                  :key="personaIndex"
                  class="mb-6"
                >
                  <BrandRankingsHeatmap
                    :queries="personaGroup.queries"
                    :personaName="personaGroup.personaName"
                    :primaryBrandName="primaryBrandName"
                  />
                </div>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
  </div>
</template>

<script>
import axios from '@/axios.js'
import Highcharts from 'highcharts'
import { Chart } from 'highcharts-vue'
import { mapGetters } from 'vuex'
import BrandRankingsHeatmap from '@/components/BrandRankingsHeatmap.vue'
import HistoricalDateBanner from '@/components/HistoricalDateBanner.vue'
import { filterHistoricalPayload, isWithinHistoricalWindow } from '@/utils/historicalData'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'VisibilityTab',
  components: {
    highcharts: Chart,
    BrandRankingsHeatmap,
    HistoricalDateBanner
  },
  props: {
    queries: {
      type: Array,
      default: () => []
    },
    personaProfiles: {
      type: Array,
      default: () => []
    },
    reviewEntityId: {
      type: Number,
      default: null
    },
    selectedProvider: {
      type: String,
      default: null
    },
    primaryBrandName: {
      type: String,
      default: null
    },
    brandMatchAliases: {
      type: Array,
      default: () => []
    },
    selectedHistoricalDate: {
      type: String,
      default: null
    },
    loadingSnapshot: {
      type: Boolean,
      default: false
    },
    snapshotLoadTarget: {
      type: String,
      default: null
    },
    showSnapshotBanner: {
      type: Boolean,
      default: false
    },
    snapshotError: {
      type: String,
      default: null
    }
  },
  emits: ['brand-match-aliases-updated', 'visibility-rescored', 'historical-date-selected', 'historical-date-cleared'],
  data() {
    return {
      selectedPersona: 'all',
      historicalData: [],
      historicalDataByPersona: [],
      loadingHistorical: false,
      chartOptions: null,
      expansionPanel: [0],
      localAliases: [],
      newAliasInput: '',
      savingAliases: false,
      aliasError: null,
      rescoringVisibility: false,
      rescoreMessage: null,
      rescoreError: false
    }
  },
  computed: {
    ...mapGetters(['lacksHistoricalTrendAccess']),
    canAddAlias() {
      if (this.savingAliases || !this.reviewEntityId) return false
      return this.normalizeAlias(this.newAliasInput).length > 0 && !this.isDuplicateAlias(this.newAliasInput)
    },
    personaOptions() {
      const options = [{ title: 'All', value: 'all' }]
      if (this.personaProfiles && this.personaProfiles.length > 0) {
        this.personaProfiles.forEach((persona, index) => {
          options.push({
            title: persona.name || `Persona ${index + 1}`,
            value: index
          })
        })
      }
      return options
    },
    filteredQueries() {
      if (this.selectedPersona === 'all') {
        return this.queries
      }
      // Filter queries by persona index (3 queries per persona)
      const personaIndex = parseInt(this.selectedPersona)
      const startIndex = personaIndex * 3
      const endIndex = startIndex + 3
      return this.queries.slice(startIndex, endIndex)
    },
    hasData() {
      return this.queries && this.queries.length > 0
    },
    totalQueries() {
      // When filtering by persona, show 3 (queries per persona)
      // When "All" is selected, show total queries
      if (this.selectedPersona === 'all') {
        return this.queries.length
      }
      return this.filteredQueries.length
    },
    currentHistoricalData() {
      // When "All" personas are selected or per-persona data is unavailable, use overall series
      if (this.selectedPersona === 'all' || !this.historicalDataByPersona.length) {
        return this.historicalData
      }

      const personaIndex = parseInt(this.selectedPersona)
      if (Number.isNaN(personaIndex)) return this.historicalData

      const personaSeries = this.historicalDataByPersona.find(
        p => p.persona_index === personaIndex
      )

      return personaSeries ? personaSeries.data || [] : []
    },
    queriesWithDetails() {
      if (!this.hasData) return []
      
      return this.filteredQueries.map(query => {
        const visibilityMetrics = query.scores?.visibility_metrics || {}
        const aiRankingPosition = visibilityMetrics.ai_ranking_position || 0
        // Check if brand is ranked by looking for primary: true in ai_rankings
        const aiRankings = query.scores?.ai_rankings || []
        const isBrandRanked = aiRankings.some(r => r.primary === true)
        const totalRankings = aiRankings.length || 0
        
        return {
          ...query,
          isBrandRanked,
          aiRankingPosition,
          totalRankings
        }
      })
    },
    queriesByPersona() {
      if (!this.hasData) return []
      
      // Group all queries by persona (3 queries per persona)
      const groups = []
      const totalPersonas = Math.ceil(this.queries.length / 3)
      
      for (let i = 0; i < totalPersonas; i++) {
        const startIndex = i * 3
        const endIndex = startIndex + 3
        const personaQueries = this.queries.slice(startIndex, endIndex)
        
        // Process queries with details
        const queriesWithDetails = personaQueries.map(query => {
          const visibilityMetrics = query.scores?.visibility_metrics || {}
          const aiRankingPosition = visibilityMetrics.ai_ranking_position || 0
          // Check if brand is ranked by looking for primary: true in ai_rankings
          const aiRankings = query.scores?.ai_rankings || []
          const isBrandRanked = aiRankings.some(r => r.primary === true)
          
          return {
            ...query,
            isBrandRanked,
            aiRankingPosition
          }
        })
        
        groups.push({
          personaIndex: i,
          queries: queriesWithDetails
        })
      }
      
      return groups
    },
    queriesWithBrand() {
      // Brand is mentioned if it appears in rankings (has primary: true in ai_rankings)
      return this.queriesWithDetails.filter(q => q.isBrandRanked === true).length
    },
    assessmentPromptCoverageRate() {
      if (this.totalQueries === 0) return 0
      return Math.round((this.queriesWithBrand / this.totalQueries) * 100)
    },
    queriesRanked() {
      return this.queriesWithDetails.filter(q => q.aiRankingPosition > 0).length
    },
    unrankedQueriesCount() {
      return this.queriesWithDetails.filter(q => q.aiRankingPosition === 0).length
    },
    averageRankingPosition() {
      const rankedQueries = this.queriesWithDetails.filter(q => q.aiRankingPosition > 0)
      if (rankedQueries.length === 0) return 0
      const sum = rankedQueries.reduce((acc, q) => acc + q.aiRankingPosition, 0)
      return sum / rankedQueries.length
    },
    penalizedAverageRankingPosition() {
      // For Category Ownership calculation: penalize unranked queries
      if (this.queriesWithDetails.length === 0) return 0
      
      // Minimum penalty position (used when no rankings exist or as fallback)
      const MIN_PENALTY_POSITION = 10
      
      const sum = this.queriesWithDetails.reduce((acc, q) => {
        if (q.aiRankingPosition > 0) {
          // Brand is ranked, use actual position
          return acc + q.aiRankingPosition
        } else {
          // Brand is not ranked - apply dynamic penalty based on number of rankings
          // Penalty = last position + 1, with minimum of MIN_PENALTY_POSITION
          // This ensures penalty is always worse than any ranked position
          const penaltyPosition = q.totalRankings > 0 
            ? Math.max(q.totalRankings + 1, MIN_PENALTY_POSITION)
            : MIN_PENALTY_POSITION
          return acc + penaltyPosition
        }
      }, 0)
      
      return sum / this.queriesWithDetails.length
    },
    aiRankingScore() {
      // Convert ranking position to a score (lower position = higher score)
      // Position 1 = 100, Position 2 = 90, Position 3 = 80, etc.
      // Formula: score = max(0, 100 - (position - 1) * 10)
      // Use penalized average for Category Ownership calculation
      if (this.penalizedAverageRankingPosition === 0) return 0
      return Math.max(0, 100 - (this.penalizedAverageRankingPosition - 1) * 10)
    },
    categoryOwnershipScore() {
      // Category Ownership = (0.6 × AI Ranking Score) + (0.4 × Share of Voice)
      // AI Ranking Score now uses penalized average (includes penalty for unranked queries)
      const aiRankingComponent = 0.6 * this.aiRankingScore
      const shareOfVoiceComponent = 0.4 * this.assessmentPromptCoverageRate
      return aiRankingComponent + shareOfVoiceComponent
    },
    personaHeatmapGroups() {
      if (!this.hasData || !this.personaProfiles) return []
      
      // Group queries by persona (3 queries per persona) for heatmap
      const groups = []
      const totalPersonas = Math.ceil(this.queries.length / 3)

      // Determine which persona indices to include based on filter
      const personaIndices =
        this.selectedPersona === 'all'
          ? Array.from({ length: totalPersonas }, (_, i) => i)
          : [parseInt(this.selectedPersona)]
      
      for (const i of personaIndices) {
        if (i < 0 || i >= totalPersonas) continue
        const startIndex = i * 3
        const endIndex = startIndex + 3
        const personaQueries = this.queries.slice(startIndex, endIndex)
        
        // Get persona name
        const personaName = this.getPersonaName(i)
        
        groups.push({
          personaIndex: i,
          personaName: personaName,
          queries: personaQueries
        })
      }
      
      return groups
    }
  },
  methods: {
    getCoverageColorClass(rate) {
      if (rate >= 70) return 'text-success'
      if (rate >= 40) return 'text-info'
      if (rate >= 20) return 'text-warning'
      return 'text-error'
    },
    getMentionShareColorClass(count) {
      // Color based on average mentions per response
      const avg = this.averageMentionsPerResponse
      if (avg >= 3) return 'text-success'
      if (avg >= 1) return 'text-info'
      if (avg > 0) return 'text-warning'
      return 'text-error'
    },
    getRankingPositionColorClass(avgPosition) {
      // Color based on average ranking position (lower is better)
      if (avgPosition === 0) return 'text-error'
      if (avgPosition <= 2) return 'text-success'
      if (avgPosition <= 5) return 'text-info'
      if (avgPosition <= 10) return 'text-warning'
      return 'text-error'
    },
    getCategoryOwnershipColorClass(score) {
      // Color based on category ownership score (higher is better)
      if (score >= 70) return 'text-success'
      if (score >= 40) return 'text-info'
      if (score >= 20) return 'text-warning'
      return 'text-error'
    },
    getPersonaName(personaIndex) {
      if (this.personaProfiles && this.personaProfiles[personaIndex]) {
        return this.personaProfiles[personaIndex].name || `Persona ${personaIndex + 1}`
      }
      return `Persona ${personaIndex + 1}`
    },
    onChartPointClick(pointIndex) {
      const point = this.currentHistoricalData[pointIndex]
      if (!point?.date || !isWithinHistoricalWindow(point.date)) return
      this.$emit('historical-date-selected', point.date)
    },
    async fetchHistoricalData() {
      if (!this.reviewEntityId) return
      
      this.loadingHistorical = true
      try {
        const params = this.selectedProvider ? { llm_provider: this.selectedProvider } : {}
        const response = await axios.get(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/historical_category_ownership`,
          {
            headers: { Authorization: localStorage.getItem('auth_token') },
            params
          }
        )
        
        if (response.data.success && response.data.data) {
          const payload = filterHistoricalPayload(response.data.data)
          this.historicalData = payload.historical_data
          this.historicalDataByPersona = payload.historical_data_by_persona
        }
      } catch (error) {
        console.error('Error fetching historical data:', error)
        this.historicalData = []
        this.historicalDataByPersona = []
      } finally {
        this.loadingHistorical = false
      }
    },
    updateChart() {
      if (this.currentHistoricalData.length <= 1) return
      
      const component = this
      const chartData = this.currentHistoricalData.map((d) => {
        const isSelected = d.date === this.selectedHistoricalDate
        return {
          x: new Date(d.date).getTime(),
          y: d.category_ownership,
          marker: {
            enabled: isSelected,
            radius: isSelected ? 6 : 4,
            fillColor: '#1976d2',
            lineColor: '#ffffff',
            lineWidth: 2
          }
        }
      })
      
      const chartContainer = document.getElementById('categoryOwnershipChart')
      if (!chartContainer) return
      
      Highcharts.chart('categoryOwnershipChart', {
        chart: {
          type: 'area',
          height: 300,
          backgroundColor: '#ffffff',
          spacing: [20, 20, 20, 20]
        },
        title: {
          text: null
        },
        xAxis: {
          type: 'datetime',
          title: {
            enabled: false
          },
          gridLineColor: '#e0e0e0',
          lineColor: '#e0e0e0',
          tickColor: '#e0e0e0',
          labels: {
            style: {
              color: '#666666'
            }
          }
        },
        yAxis: {
          title: {
            enabled: false
          },
          min: 0,
          max: 100,
          tickPositions: [0, 25, 50, 75, 100],
          gridLineColor: '#e0e0e0',
          lineColor: '#e0e0e0',
          tickColor: '#e0e0e0',
          labels: {
            style: {
              color: '#666666'
            }
          }
        },
        tooltip: {
          backgroundColor: '#ffffff',
          borderColor: '#e0e0e0',
          borderRadius: 4,
          shadow: false,
          style: {
            color: '#333333'
          },
          formatter: function() {
            const dateStr = Highcharts.dateFormat('%Y-%m-%d', this.x)
            return `${dateStr}<br/><b>${this.y.toFixed(1)}%</b><br/><span style="font-size: 11px; color: #666;">Click to view this date</span>`
          }
        },
        plotOptions: {
          area: {
            cursor: 'pointer',
            marker: {
              enabled: false,
              radius: 4,
              fillColor: '#1976d2',
              lineColor: '#ffffff',
              lineWidth: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            },
            lineWidth: 2,
            fillColor: 'rgba(25, 118, 210, 0.2)',
            states: {
              hover: {
                lineWidth: 2
              }
            },
            point: {
              events: {
                click: function() {
                  component.onChartPointClick(this.index)
                }
              }
            }
          }
        },
        series: [{
          name: 'Category Ownership',
          data: chartData,
          color: '#1976d2'
        }],
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        }
      })
    },
    normalizeAlias(value) {
      return (value || '').trim()
    },
    aliasKey(value) {
      return this.normalizeAlias(value).toLowerCase()
    },
    isDuplicateAlias(value) {
      const key = this.aliasKey(value)
      if (!key) return true
      if (this.primaryBrandName && key === this.aliasKey(this.primaryBrandName)) return true
      return this.localAliases.some((alias) => this.aliasKey(alias) === key)
    },
    syncLocalAliases(aliases) {
      this.localAliases = Array.isArray(aliases) ? [...aliases] : []
    },
    async persistAliases(nextAliases) {
      if (!this.reviewEntityId) return

      this.savingAliases = true
      this.aliasError = null

      try {
        const response = await axios.patch(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/update_brand_match_aliases`,
          { brand_match_aliases: nextAliases },
          { headers: { Authorization: localStorage.getItem('auth_token') } }
        )

        if (response.data.success) {
          const savedAliases = response.data.brand_match_aliases || []
          this.syncLocalAliases(savedAliases)
          this.$emit('brand-match-aliases-updated', savedAliases)
        } else {
          this.aliasError = response.data.error || 'Failed to save brand match strings'
        }
      } catch (error) {
        console.error('Error saving brand match aliases:', error)
        this.aliasError = error.response?.data?.error || 'Failed to save brand match strings'
      } finally {
        this.savingAliases = false
      }
    },
    addAlias() {
      if (!this.canAddAlias) return
      const value = this.normalizeAlias(this.newAliasInput)
      if (!value) return
      if (this.isDuplicateAlias(value)) {
        this.aliasError = 'That string is already included'
        return
      }

      const nextAliases = [...this.localAliases, value]
      this.newAliasInput = ''
      this.aliasError = null
      this.persistAliases(nextAliases)
    },
    focusAliasInput() {
      if (this.savingAliases || !this.reviewEntityId) return
      this.$refs.aliasInputRef?.focus()
    },
    async removeAlias(alias) {
      const nextAliases = this.localAliases.filter((item) => item !== alias)
      await this.persistAliases(nextAliases)
    },
    async rescoreVisibility() {
      if (!this.reviewEntityId || this.rescoringVisibility) return

      this.rescoringVisibility = true
      this.rescoreMessage = null
      this.rescoreError = false

      try {
        // Aliases apply to every LLM, so the backend rescores all providers.
        const response = await axios.post(
          `${BASE_URL}/review_entities/${this.reviewEntityId}/rescore_visibility`,
          {},
          {
            headers: { Authorization: localStorage.getItem('auth_token') }
          }
        )

        if (response.data.success) {
          const count = response.data.updated_count ?? 0
          this.rescoreMessage = `Updated ${count} scored ${count === 1 ? 'response' : 'responses'} across all LLMs.`
          this.$emit('visibility-rescored')
          await this.fetchHistoricalData()
        } else {
          this.rescoreError = true
          this.rescoreMessage = response.data.error || 'Failed to recalculate visibility'
        }
      } catch (error) {
        console.error('Error recalculating visibility:', error)
        this.rescoreError = true
        this.rescoreMessage = error.response?.data?.error || 'Failed to recalculate visibility'
      } finally {
        this.rescoringVisibility = false
      }
    }
  },
  watch: {
    brandMatchAliases: {
      immediate: true,
      handler(newVal) {
        this.syncLocalAliases(newVal)
      }
    },
    reviewEntityId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchHistoricalData()
        }
      }
    },
    selectedProvider() {
      if (this.reviewEntityId) {
        this.fetchHistoricalData()
      }
    },
    historicalData: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    },
    selectedPersona: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    },
    selectedHistoricalDate: {
      handler() {
        this.$nextTick(() => {
          this.updateChart()
        })
      }
    }
  }
}
</script>

<style scoped>
.metric-card {
  border-radius: 16px;
  border-color: var(--brand-ocean-100) !important;
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 30, 53, 0.12);
}

.persona-header-row {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.historical-chart-card {
  border-radius: 16px;
  border-color: var(--brand-sand-300) !important;
}

.brand-match-card {
  border-radius: 16px;
  border-color: var(--brand-sand-300) !important;
}

.brand-match-header__copy {
  flex: 1 1 220px;
}

.min-width-0 {
  min-width: 0;
}

.brand-match-editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  cursor: text;
}

.brand-match-editor:focus-within {
  border-color: rgba(var(--v-theme-primary), 0.45);
  box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.12);
}

.brand-match-editor--disabled {
  opacity: 0.7;
  cursor: default;
}

.brand-match-editor__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  min-height: 48px;
}

.brand-match-editor__input {
  flex: 1 1 140px;
  min-width: 120px;
  border: 0;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  padding: 4px 0;
}

.brand-match-editor__input::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.brand-match-editor__input:disabled {
  cursor: not-allowed;
}

.brand-match-editor__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  margin-left: auto;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.55);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.brand-match-editor__add:hover:not(:disabled) {
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.brand-match-editor__add:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.brand-match-chip--primary {
  font-weight: 600;
}
</style>
